import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Spin,
  Tabs,
} from 'antd';
import {
  CalculatorOutlined,
  DiffOutlined,
  ImportOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import Editor from 'components/Editor/index';
import PlainTransfer from 'components/PlainTransfer';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import CreateClave from 'utils/createClave';
import {
  deleteTipoDeActualizacion,
  getTipoDeActualizacion,
  getTiposDeActualizaciones,
  patchTipoDeActualizacion,
  postTipoDeActualizacion,
} from 'api/recaudacion/tiposDeActualizacion';
import { getCatalogoDeTarifas } from 'api/configuracion/catalogoDeTarifas';
import { getClase, getClases } from 'api/catalogos/clases';
import { getConceptoDeIngresos, getConceptosDeIngresos } from 'api/catalogos/conceptosDeIngresos';
import { getTiposDeAplicacion, getPeriodos } from 'api/recaudacion/index';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getVariables } from 'api/configuracion/variables';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

const { TextArea } = Input;
const { TabPane } = Tabs;

const catalogsModels = [
  'descuento',
  'clase',
  'periodo',
  'variable',
  'catalogodetarifa',
  'tipodedescuento',
  'conceptodeingreso',
  'tipodeaplicacion',
];

// eslint-disable-next-line react/prop-types
const TiposDeActualizacion = ({ permission }) => {
  const monedaDefault = useSelector(({ auth }) => auth.entidad?.moneda);
  const [decimal] = createDecimalRegExp(monedaDefault);
  const [formCalculo] = Form.useForm();
  const [formGeneral] = Form.useForm();
  const [formAccesorios] = Form.useForm();
  const [formVariables] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion)
    .map((e) => (e.id === 2 || e.id === 4 ? ({ ...e, disabled: true }) : e))
    .filter((e) => !e.disabled);

  const [periodos, setPeriodos] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [tiposDescuentos, setTiposDescuentos] = useState([]);
  const [app, setApp] = useState(0);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [variables, setVariables] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [clasesTemp, setClasesTemp] = useState({ results: [] });
  const [conceptosDeIngresoTemp, setConceptosDeIngresoTemp] = useState({ results: [] });

  const criSelected = Form.useWatch('clase_cri', formGeneral);

  const clases = useRef({ results: [] });
  const conceptosDeIngreso = useRef({ results: [] });

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeActualizaciones({
      concepto_de_ingreso__periodo_fiscal: periodoFiscalSelected?.id,
    });
    setData(_data);
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    formGeneral.resetFields();
    formCalculo.resetFields();
    formAccesorios.resetFields();
    formVariables.resetFields();
    setCurrentTabKey('general');
    setSelectedRowKeys([]);
    setApp(0);
    setClasesTemp({ results: [] });
    setConceptosDeIngresoTemp({ results: [] });
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const dataClases = await getClases();
      const dataPeriodos = await getPeriodos();
      const dataCatalogoDeTarifas = await getCatalogoDeTarifas();
      const dataVariables = await getVariables({ es_script: true });
      const dataTiposDeDescuentos = await getTiposDeDescuentos({ aplicacion_del_descuento: 3 });
      await getTiposDeAplicacion();

      if (mounted) {
        setClasesTemp(dataClases);
        setPeriodos(dataPeriodos);
        setVariables(dataVariables);
        setTarifas(dataCatalogoDeTarifas);
        setTiposDescuentos(dataTiposDeDescuentos);
      }

      await fetchData();
      setLoading(false);
    };
    fetchAll();
    validatePermission();

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [periodoFiscalSelected]);

  const onFinish = async () => {
    try {
      setLoading(true);
      await formGeneral.validateFields();
      await formCalculo.validateFields();
      await formAccesorios.validateFields();
      await formVariables.validateFields();
      const values = {
        ...formGeneral.getFieldsValue(),
        ...formCalculo.getFieldsValue(),
        ...formAccesorios.getFieldsValue(),
        ...formVariables.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchTipoDeActualizacion(key, values) : postTipoDeActualizacion(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [formGeneral, formCalculo, formVariables], setCurrentTabKey);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeActualizacion(key);
      if (deleted) {
        onCancel();
        setVisibleAlert(false);
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setApp(record.tipo_de_aplicacion);
    formGeneral.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
    CreateClave(data, formGeneral);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const record = await getTipoDeActualizacion(key);
    const _CRI = await getClase(record.clase_cri);
    const _concepto = await getConceptoDeIngresos(record.concepto_de_ingreso);
    setClasesTemp({ results: [_CRI] });
    setConceptosDeIngresoTemp({ results: [_concepto] });
    setApp(record.tipo_de_aplicacion);
    formGeneral.setFieldsValue(record);
    formCalculo.setFieldsValue(record);
    formAccesorios.setFieldsValue(record);
    formVariables.setFieldsValue(record);
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    porcentaje: [
      requiredRule,
      {
        validator: async (rule, val) => {
          const parsedVal = parseFloat(val);
          if (val) {
            if (!decimal.test(val)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (parsedVal > 100) {
              throw new Error('El valor no puede ser mayor a 100');
            } else if (parsedVal <= 0) {
              throw new Error('El valor debe ser mayor a 0');
            }
          }
        },
      },
    ],
    importe: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val && !decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    importe_maximo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_maximo) {
            if (!decimal.test(values.importe_maximo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_maximo <= importe_minimo) {
              throw new Error('El campo debe ser mayor al Importe Mínimo');
            }
          }
        },
      },
    ],
    importe_minimo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_minimo) {
            if (!decimal.test(values.importe_minimo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_minimo >= importe_maximo) {
              throw new Error('El campo debe ser menor al Importe Máximo');
            }
          }
        },
      },
    ],
    variables: [
      {
        required: app === 5,
      },
    ],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      render: (val) => (periodos.find((item) => item.id === val)?.descripcion),
    },

    {
      titleText: 'Fecha de Vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      key: 'fecha_de_vencimiento',
      width: 200,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => (estadosGlobales.find((item) => item.id === val)?.descripcion),
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            handleOnRowClick={handleOnRowClick}
            rowSelection={rowSelection}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            loading={loading}
            rowKey="id"
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Actualización`}
            extra={(
              <FormSubmitControls
                onFinish={() => onFinish()}
                onCancel={onCancel}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Tabs
              type="card"
              onChange={setCurrentTabKey}
              activeKey={currentTabKey}
            >
              <TabPane
                tab={(
                  <span>
                    <ImportOutlined />
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  name="general"
                  form={formGeneral}
                  onFinish={() => onFinish()}
                  initialValues={{
                    estados_globales: 1,
                    es_actualizacion_sobre_base: true,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clave"
                        label="Clave"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Input allowClear disabled={selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clase_cri"
                        label="CRI"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          dataSource={
                            clasesTemp.results?.length
                              ? clasesTemp : clases.current
                          }
                          labelProp="denominacion"
                          usingExternalDataSource={Boolean(clasesTemp.results?.length)}
                          onClear={() => {
                            formGeneral.resetFields(['concepto_de_ingreso']);
                            setClasesTemp({ results: [] });
                            setConceptosDeIngresoTemp({ results: [] });
                          }}
                          onChange={() => formGeneral.resetFields(['concepto_de_ingreso'])}
                          pagination={{
                            getter: getClases,
                            setter: (dataSource) => {
                              clases.current = dataSource;
                            },
                            config: { qName: 'denominacion' },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="concepto_de_ingreso"
                        label="Concepto de ingresos"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          dataSource={
                            conceptosDeIngresoTemp.results?.length
                              ? conceptosDeIngresoTemp : conceptosDeIngreso.current
                          }
                          labelProp="descripcion"
                          usingExternalDataSource={Boolean(conceptosDeIngresoTemp.results?.length)}
                          onClear={() => {
                            formGeneral.resetFields(['concepto_de_ingreso']);
                            setConceptosDeIngresoTemp({ results: [] });
                          }}
                          disabled={!formGeneral.getFieldValue('clase_cri')}
                          pagination={{
                            getter: getConceptosDeIngresos,
                            setter: (dataSource) => {
                              conceptosDeIngreso.current = dataSource;
                            },
                            config: { qName: 'descripcion' },
                            params: { cri_clase: criSelected },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="es_actualizacion_sobre_base"
                        label="Actualización sobre base"
                        hasFeedback
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={16}
                    >
                      <Form.Item
                        name="descripcion"
                        label="Descripción"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="periodo"
                        rules={rules.required}
                        label="Periodo"
                        hasFeedback
                      >
                        <Select dataSource={periodos} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={16}
                    >
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                      >
                        <TextArea allowClear maxLength={200} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          dataSource={estadosGlobales}
                          disabled={!selectedRowKeys.length}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <CalculatorOutlined />
                    Parámetros de Cálculo
                  </span>
                )}
                key="calculo"
                forceRender
              >
                <Form
                  name="calculo"
                  layout="vertical"
                  form={formCalculo}
                  onFinish={() => onFinish()}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="fecha_de_vencimiento"
                        label="Fecha de vencimiento"
                        hasFeedback
                        rules={rules.required}
                      >
                        <DatePicker
                          placeholder=""
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <NumericInput
                        name="dias_de_gracia"
                        label="Días de gracia"
                      />
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="tipo_de_aplicacion"
                        label="Tipo de aplicación"
                        hasFeedback
                      >
                        <Select
                          onChange={setApp}
                          dataSource={tiposDeAplicacion}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={10}>
                    {app === 1 && (
                      <>
                        <Col
                          xs={0}
                          sm={0}
                          md={16}
                        />
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="porcentaje"
                            label="Porcentaje"
                            rules={rules.porcentaje}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {app === 3 && (
                      <>
                        <Col
                          xs={0}
                          sm={0}
                          md={16}
                        />
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe"
                            label="Importe"
                            rules={rules.importe}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {app === 4 && (
                      <>
                        <Col
                          xs={0}
                          sm={0}
                          md={8}
                        />
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            rules={rules.importe_minimo}
                            name="importe_minimo"
                            label="Importe mínimo"
                            decimal
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            rules={rules.importe_maximo}
                            name="importe_maximo"
                            label="Importe máximo"
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {app === 5 && (
                      <Editor
                        rules={rules.required}
                        form={formCalculo}
                        actualizacion
                        variables={variables}
                      />
                    )}
                    {app === 6 && (
                      <>
                        <Col
                          xs={0}
                          sm={0}
                          md={16}
                        />
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            name="catalogo_de_tarifa"
                            rules={rules.required}
                            label="Tarifa"
                            hasFeedback
                          >
                            <Select dataSource={tarifas} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {app === 7 && (
                    <Editor
                      rules={rules.required}
                      form={formCalculo}
                      actualizacion
                      variables={variables}
                    />
                    )}
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DiffOutlined />
                    Accesorios
                  </span>
                )}
                key="accesorios"
              >
                <Form
                  layout="vertical"
                  form={formAccesorios}
                  onFinish={() => onFinish()}
                  name="accesorios"
                >
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tiposDescuentos}
                      label="Tipo de descuento"
                      formItemName="tipos_de_descuento"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Variables
                  </span>
                )}
                key="variables"
                forceRender
                disabled={app !== 5}
              >
                <Form
                  form={formVariables}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="variables"
                >
                  <Row>
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={variables}
                        label="Variables"
                        formItemName="variables"
                        form={formVariables}
                        filterProp="descripcion_de_variable"
                      />
                    </Col>
                  </Row>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Actualización ${formGeneral.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDeActualizacion;
